import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="glass-effect sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link">
            <img
              src="/WhiteLogo.png"
              alt="Alvron Partners"
              className="h-10 md:h-12"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-8" data-testid="privacy-title">
          Privacy Policy
        </h1>
        <p className="text-muted-foreground mb-12">
          Last Updated: January 30, 2026
        </p>

        <div className="prose prose-invert max-w-none space-y-8 text-muted-foreground">
          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">1. Introduction</h2>
            <p>
              Alvron Partners ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.alvronpartners.com, purchase our digital products and services, or engage with our investment banking advisory services.
            </p>
            <p>
              Please read this Privacy Policy carefully. By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access the site or purchase our products.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl text-foreground mt-6 mb-3">2.1 Personal Information</h3>
            <p>We may collect personally identifiable information that you voluntarily provide to us when you:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Register for an account or purchase digital products</li>
              <li>Fill out a contact form or request a consultation</li>
              <li>Subscribe to our newsletter or marketing communications</li>
              <li>Engage our advisory services</li>
              <li>Make a payment through our platform</li>
            </ul>
            <p className="mt-4">This information may include:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Full name and title</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Billing address</li>
              <li>Payment information (credit card numbers, billing details)</li>
              <li>Company/organization name</li>
              <li>Professional information relevant to our services</li>
            </ul>

            <h3 className="text-xl text-foreground mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <p>When you access our website, we automatically collect certain information, including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>IP address and geographic location</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Access times and dates</li>
              <li>Pages viewed and referring URLs</li>
              <li>Device identifiers</li>
            </ul>

            <h3 className="text-xl text-foreground mt-6 mb-3">2.3 Payment Information</h3>
            <p>
              When you make a purchase of our digital goods and services, we collect payment information necessary to process your transaction. This may include credit card numbers, bank account details, and billing information. We use secure, PCI-DSS compliant payment processors to handle all payment transactions. We do not store complete credit card information on our servers.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong className="text-foreground">To provide services:</strong> Process transactions, deliver digital products, and provide advisory services</li>
              <li><strong className="text-foreground">To communicate:</strong> Send order confirmations, respond to inquiries, and provide customer support</li>
              <li><strong className="text-foreground">To improve our services:</strong> Analyze usage patterns and enhance user experience</li>
              <li><strong className="text-foreground">For marketing:</strong> Send promotional materials and newsletters (with your consent)</li>
              <li><strong className="text-foreground">For compliance:</strong> Meet legal obligations and enforce our terms</li>
              <li><strong className="text-foreground">For security:</strong> Detect and prevent fraud, unauthorized access, and other illegal activities</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">4. Disclosure of Your Information</h2>
            <p>We may share your information in the following situations:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong className="text-foreground">Service Providers:</strong> Third-party vendors who assist in operating our website, processing payments, and delivering services</li>
              <li><strong className="text-foreground">Payment Processors:</strong> Secure payment gateways to process your transactions</li>
              <li><strong className="text-foreground">Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong className="text-foreground">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong className="text-foreground">With Your Consent:</strong> When you have given us explicit permission</li>
            </ul>
            <p className="mt-4">
              We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">5. Digital Products and Services</h2>
            <p>
              When you purchase digital goods from Alvron Partners, we collect and process information necessary to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Verify your identity and prevent fraud</li>
              <li>Process your payment securely</li>
              <li>Deliver your digital products via email or secure download</li>
              <li>Provide access to digital services and platforms</li>
              <li>Send purchase receipts and transaction records</li>
              <li>Handle refund requests in accordance with our refund policy</li>
            </ul>
            <p className="mt-4">
              Your purchase history and digital product access information is retained to provide ongoing service and support.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">6. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to collect and track information about your browsing activities. You can set your browser to refuse all or some browser cookies, but this may limit your ability to use certain features of our website.
            </p>
            <p className="mt-4">Types of cookies we use:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong className="text-foreground">Essential Cookies:</strong> Required for website functionality</li>
              <li><strong className="text-foreground">Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong className="text-foreground">Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">7. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>SSL/TLS encryption for data transmission</li>
              <li>PCI-DSS compliant payment processing</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication measures</li>
              <li>Secure data storage with encryption at rest</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">8. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. For digital product purchases, we retain transaction records for a minimum of 7 years for tax and legal compliance purposes.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">9. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong className="text-foreground">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-foreground">Correction:</strong> Request correction of inaccurate data</li>
              <li><strong className="text-foreground">Deletion:</strong> Request deletion of your personal data</li>
              <li><strong className="text-foreground">Portability:</strong> Request transfer of your data to another service</li>
              <li><strong className="text-foreground">Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong className="text-foreground">Withdraw Consent:</strong> Withdraw previously given consent</li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, please contact us at contact@alvronpartners.com.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">10. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">11. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete that information.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">12. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than India. By using our services, you consent to the transfer of your information to countries that may have different data protection laws than your country of residence.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">14. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
            <div className="mt-4 p-6 bg-card border border-white/5">
              <p><strong className="text-foreground">Alvron Partners</strong></p>
              <p className="mt-2">18, Rajan Pada Mindspace</p>
              <p>Malad West, Mumbai, India</p>
              <p className="mt-2">Email: contact@alvronpartners.com</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-card py-8 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Alvron Partners. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="text-muted-foreground hover:text-foreground text-sm transition-colors">
              Terms & Conditions
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
